import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// import moment from "moment";
import Spinner from "./spinner";
import { obtenerInformacionEstadoPedido } from "../api/api-estado";
import { sharedNormalizeHexColor } from "../utils/shared-functions";

/**
 * Stacked above ModalMontoPagadoAConductor (higher z-index).
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {object|null} item — Row from estadoPagoInfo (descripcion, nombreCliente, numeroDocumento, detalle, …)
 * @param {object} parentData — Payload passed to ModalMontoPagadoAConductor (fecha, labelPlaca, estadoPagoInfo, …)
 */
export default function ModificarEstadoPedidoModal({
    isOpen,
    onClose,
    item = null,
    parentData = {},
    showToast,
    setProcessingVisible,
    indexWorkflow = null,
    onUpdateMontoPago = null,
    onActualizarWorkFlowEnServer = null,
}) {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [infoWorkFlow, setInfoWorkFlow] = useState(null);
    const [detallePago, setDetallePago] = useState([]);

    useEffect(() => {
        if (isOpen === false) {
            setInfoWorkFlow(null);
            setLoading(false);
            return;
        }
        if (isOpen === true && item) {
            obtenerInfoWorkFlow();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !item?.detalle || !Array.isArray(item.detalle)) {
            setDetallePago([]);
            return;
        }
        setDetallePago(
            item.detalle.map((d) => {
                const raw = d.monto;
                const n = raw == null || raw === "" ? null : Number(raw);
                return {
                    descripcion: d.descripcion ?? "",
                    monto: n != null && Number.isFinite(n) ? n : null,
                };
            })
        );
    }, [isOpen, item]);

    const obtenerInfoWorkFlow = async () => {
        try {
            setLoading(true);
            setInfoWorkFlow(null);
            const objEnviar = {
                idEstado: item.idEstado,
                idSubEstado: item.idSubEstado,
            };
            const response = await obtenerInformacionEstadoPedido(objEnviar);
            if (response.error === false) {
                setInfoWorkFlow(response.body);
            } else {
                setInfoWorkFlow(null);
            }
        }
        catch (err) {
            console.log('obtenerInfoWorkFlow err');
            console.log(err);
            setInfoWorkFlow(null);
        }
        finally {
            setLoading(false);
        }
    };

    const handleUpdateClick = async () => {
        try {
            if (typeof onActualizarWorkFlowEnServer === "function") {
                onActualizarWorkFlowEnServer();
            }
        } catch (err) {
            console.log("ModificarEstadoPedidoModal onUpdate err");
            console.log(err);
        } finally {
            onClose();
        }
    };

    const handleDetalleMontoInputChange = (indexDetallePago, rawValue) => {
        try {
            const str = String(rawValue).trim();
            const parsed = str === "" ? NaN : parseFloat(str.replace(",", "."));
            const monto = Number.isFinite(parsed) ? Number.parseFloat(parsed.toFixed(2)) : null;
            setDetallePago((prev) =>
                prev.map((row, i) =>
                    i === indexDetallePago ? { ...row, monto } : row
                )
            );
            if (typeof onUpdateMontoPago === "function") {
                queueMicrotask(() =>
                    onUpdateMontoPago(
                        indexDetallePago,
                        monto == null ? 0 : monto,
                        indexWorkflow
                    )
                );
            }
        } catch (error) {
            console.log("handleDetalleMontoInputChange err");
            console.log(error);
        }
    };

    if (!isOpen || !item) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modificar-estado-pedido-title"
        >
            <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={onClose}
                aria-hidden="true"
            />

            <div
                className="relative bg-white rounded-lg shadow-lg w-[min(92vw,56rem)] sm:w-[85%] lg:w-[60%] max-w-[90%] max-h-[500px] overflow-y-auto p-4 sm:p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <h2
                    id="modificar-estado-pedido-title"
                    className="text-lg font-semibold text-gray-900 mb-4"
                >
                    {t("label_modificar_estado_pedido")}
                </h2>

                {(parentData.labelPlaca) && (
                    <>
                        <div className="mb-0 space-y-1 text-sm text-gray-600 border-b border-gray-100 pb-3">
                            {parentData.labelPlaca != null && parentData.labelPlaca !== "" && (
                                <div>
                                    <span className="font-medium text-gray-700">{t("label_placa")}: </span>
                                    {parentData.labelPlaca}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col ">
                            <label className="text-sm font-semibold text-gray-900 mb-4">
                                {item.nombreCliente}
                            </label>
                            <label className="text-sm font-semibold text-gray-900 mb-4">
                                {(t("label_numero_documento"))}: {item.numeroDocumento}
                            </label>
                        </div>
                    </>
                )}
                <hr />

                {loading === true && infoWorkFlow === null &&
                    <div className="flex items-center gap-2">
                        <Spinner size="md" color="blue" />
                        <span className="text-gray-700 font-semibold">{t("label_cargando_informacion")}</span>
                    </div>
                }

                {loading === false && infoWorkFlow !== null && (
                    <div className="space-y-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                            <div className="min-w-0 flex-1">
                                <label
                                    htmlFor="modificar-estado-pedido-estado"
                                    className="mb-1 block text-xs font-medium text-gray-600"
                                >
                                    {t("column_estado")}
                                </label>
                                <div className="flex overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
                                    <span
                                        className="w-1.5 shrink-0 self-stretch"
                                        style={{
                                            backgroundColor: sharedNormalizeHexColor(infoWorkFlow.color),
                                        }}
                                        title={sharedNormalizeHexColor(infoWorkFlow.color)}
                                        aria-hidden
                                    />
                                    <input
                                        id="modificar-estado-pedido-estado"
                                        readOnly
                                        value={infoWorkFlow.descripcionEstado ?? ""}
                                        className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-0"
                                    />
                                    <span
                                        className="mr-2 flex shrink-0 items-center self-center rounded border border-gray-200 px-1.5 py-0.5 font-mono text-[10px] uppercase text-gray-500"
                                        title={t("column_color")}
                                    >
                                        {sharedNormalizeHexColor(infoWorkFlow.color)}
                                    </span>
                                </div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <label
                                    htmlFor="modificar-estado-pedido-subestado"
                                    className="mb-1 block text-xs font-medium text-gray-600"
                                >
                                    {t("column_sub_estado")}
                                </label>
                                <input
                                    id="modificar-estado-pedido-subestado"
                                    readOnly
                                    value={infoWorkFlow.descripcionSubEstado ?? ""}
                                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none"
                                />
                            </div>
                        </div>

                        {detallePago.length > 0 && (
                            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
                                <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {t("label_detalle_pagos")}
                                </span>
                                <div className="space-y-3">
                                    {detallePago.map((line, idx) => (
                                        <div
                                            key={idx}
                                            className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <label
                                                    className="mb-1 block text-xs font-medium text-gray-600"
                                                    htmlFor={`modificar-detalle-desc-${idx}`}
                                                >
                                                    {t("column_descripcion")}
                                                </label>
                                                <input
                                                    id={`modificar-detalle-desc-${idx}`}
                                                    readOnly
                                                    value={line.descripcion}
                                                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                                                />
                                            </div>
                                            <div className="w-full sm:w-40 shrink-0">
                                                <label
                                                    className="mb-1 block text-xs font-medium text-gray-600"
                                                    htmlFor={`modificar-detalle-monto-${idx}`}
                                                >
                                                    {t("column_monto")}
                                                </label>
                                                <input
                                                    id={`modificar-detalle-monto-${idx}`}
                                                    type="number"
                                                    value={line.monto ?? ""}
                                                    onChange={(e) =>
                                                        handleDetalleMontoInputChange(idx, e.target.value)
                                                    }
                                                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm tabular-nums text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {loading === false && infoWorkFlow === null &&
                    <div>
                        <div>
                            <span className="text-gray-700 font-semibold">{t("label_no_hay_informacion_disponible")}: </span>
                        </div>
                    </div>
                }

                <div className="mt-6 flex flex-wrap justify-between gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium text-gray-800"
                    >
                        {t("label_cerrar")}
                    </button>
                    <button
                        type="button"
                        onClick={handleUpdateClick}
                        className="px-4 py-2 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                    >
                        {t("label_actualizar")}
                    </button>
                </div>
            </div>
        </div>
    );
}
